import React, { useEffect, useState } from "react";

type DesktopPlatform = "mac" | "windows";
type DesktopChannel = "dev" | "prod";

interface ReleaseAsset {
  name: string;
  browser_download_url: string;
  size: number;
}

interface Release {
  tag_name: string;
  name: string;
  published_at: string;
  html_url: string;
  assets: ReleaseAsset[];
}

const GITHUB_RELEASES_URL = "https://github.com/YouWoAI/YouWo/releases";
const RELEASE_API_URLS = [
  "https://api.github.com/repos/YouWoAI/YouWo/releases?per_page=30",
  "https://api.github.com/repos/YouWoAI/YouWoAI-Mobile/releases?per_page=30",
];

const DIRECT_DOWNLOAD_URLS: Record<DesktopChannel, Record<DesktopPlatform, string>> = {
  dev: {
    mac: "https://downloads.youwo.ai/mac-dev/YouWo-dev-latest.dmg",
    windows: "https://downloads.youwo.ai/win-dev/YouWo-dev-latest.exe",
  },
  prod: {
    mac: "https://downloads.youwo.ai/mac/YouWo-latest.dmg",
    windows: "https://downloads.youwo.ai/win/YouWo-latest.exe",
  },
};

const formatBytes = (bytes: number) => {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDate = (iso: string) => {
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
};

const detectPreferredPlatform = (): DesktopPlatform => {
  const navigatorLike =
    typeof window !== "undefined"
      ? (window.navigator as Navigator & { userAgentData?: { platform?: string } })
      : undefined;
  const platform = navigatorLike?.userAgentData?.platform || navigatorLike?.platform || "";
  const userAgent = navigatorLike?.userAgent || "";

  return /windows|win32|win64|wow64/i.test(`${platform} ${userAgent}`) ? "windows" : "mac";
};

const getPlatformLabel = (platform: DesktopPlatform) =>
  platform === "windows" ? "Windows EXE" : "Mac DMG";

const getInstallerAsset = (release: Release | null, platform: DesktopPlatform) => {
  const extension = platform === "windows" ? ".exe" : ".dmg";
  return release?.assets.find((asset) => {
    const name = asset.name.toLowerCase();
    return name.endsWith(extension) && !name.endsWith(".blockmap");
  });
};

const getDownloadTarget = (
  release: Release | null,
  channel: DesktopChannel,
  platform: DesktopPlatform,
) => {
  const asset = getInstallerAsset(release, platform);
  return {
    label: getPlatformLabel(platform),
    url: asset?.browser_download_url || DIRECT_DOWNLOAD_URLS[channel][platform],
    size: asset?.size,
  };
};

const isDevRelease = (release: Release) =>
  release.tag_name.startsWith("mobile/release-dev-") ||
  release.tag_name.startsWith("release-dev-");

const isProdRelease = (release: Release) =>
  /^mobile\/release-\d/.test(release.tag_name) || /^release-\d/.test(release.tag_name);

const hasDesktopAsset = (release: Release) =>
  Boolean(getInstallerAsset(release, "mac") || getInstallerAsset(release, "windows"));

const sortReleasesByPublishedAtDesc = (releases: Release[]) =>
  [...releases].sort(
    (a, b) => new Date(b.published_at).getTime() - new Date(a.published_at).getTime(),
  );

const DownloadDesktop: React.FC = () => {
  const [devRelease, setDevRelease] = useState<Release | null>(null);
  const [prodRelease, setProdRelease] = useState<Release | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [preferredPlatform] = useState<DesktopPlatform>(() => detectPreferredPlatform());

  useEffect(() => {
    const fetchReleases = async () => {
      try {
        let releases: Release[] = [];
        for (const apiUrl of RELEASE_API_URLS) {
          const res = await fetch(apiUrl);
          if (!res.ok) continue;
          const repoReleases: Release[] = await res.json();
          releases = releases.concat(repoReleases);
        }
        if (releases.length === 0) {
          setError("Could not fetch GitHub releases. Use the direct links below.");
          setLoading(false);
          return;
        }

        const sortedReleases = sortReleasesByPublishedAtDesc(releases);
        const latestDev = sortedReleases.find((release) => isDevRelease(release) && hasDesktopAsset(release));
        const latestProd = sortedReleases.find((release) => isProdRelease(release) && hasDesktopAsset(release));

        setDevRelease(latestDev || null);
        setProdRelease(latestProd || null);
      } catch {
        setError("Could not fetch releases. Use the direct links below.");
      }
      setLoading(false);
    };
    fetchReleases();
  }, []);

  const renderDownloadButtons = (release: Release | null, channel: DesktopChannel) => {
    const secondaryPlatform = preferredPlatform === "windows" ? "mac" : "windows";
    const primaryTarget = getDownloadTarget(release, channel, preferredPlatform);
    const secondaryTarget = getDownloadTarget(release, channel, secondaryPlatform);

    return (
      <div style={styles.buttonRow}>
        <a
          href={primaryTarget.url}
          style={{ ...styles.button, ...styles.buttonPrimary }}
        >
          Download {primaryTarget.label}
          {primaryTarget.size ? ` (${formatBytes(primaryTarget.size)})` : ""}
        </a>
        <a
          href={secondaryTarget.url}
          style={{ ...styles.button, ...styles.buttonSecondary }}
        >
          {secondaryTarget.label}
          {secondaryTarget.size ? ` (${formatBytes(secondaryTarget.size)})` : ""}
        </a>
        {release ? (
          <a href={release.html_url} style={styles.link} target="_blank" rel="noreferrer">
            Release Notes
          </a>
        ) : null}
      </div>
    );
  };

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <h1 style={styles.heading}>YouWoAI Desktop</h1>
        <p style={styles.subtitle}>macOS (Apple Silicon) and Windows (x64)</p>

        {loading && <p style={styles.loadingText}>Loading releases...</p>}

        {/* Dev Build */}
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <span style={{ ...styles.badge, ...styles.badgeDev }}>DEV</span>
            <h2 style={styles.cardTitle}>Development Build</h2>
          </div>
          <p style={styles.cardDesc}>
            Points to <code style={styles.code}>api-server.dev.youwo.ai</code>. For internal testing.
          </p>
          {devRelease ? (
            <div style={styles.releaseInfo}>
              <p style={styles.releaseTag}>
                {devRelease.tag_name} &middot; {formatDate(devRelease.published_at)}
              </p>
              {renderDownloadButtons(devRelease, "dev")}
            </div>
          ) : (
            !loading && (
              renderDownloadButtons(null, "dev")
            )
          )}
        </div>

        {/* Prod Build */}
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <span style={{ ...styles.badge, ...styles.badgeProd }}>PROD</span>
            <h2 style={styles.cardTitle}>Production Build</h2>
          </div>
          <p style={styles.cardDesc}>
            Points to <code style={styles.code}>api-server.youwo.ai</code>. For release distribution.
          </p>
          {prodRelease ? (
            <div style={styles.releaseInfo}>
              <p style={styles.releaseTag}>
                {prodRelease.tag_name} &middot; {formatDate(prodRelease.published_at)}
              </p>
              {renderDownloadButtons(prodRelease, "prod")}
            </div>
          ) : (
            !loading && (
              renderDownloadButtons(null, "prod")
            )
          )}
        </div>

        {/* Info */}
        <div style={styles.infoSection}>
          <h3 style={styles.infoTitle}>How it works</h3>
          <ul style={styles.infoList}>
            <li>
              Push a <code style={styles.code}>mobile/release-dev-X.Y.Z</code> tag to build dev web plus desktop installers
            </li>
            <li>
              Push a <code style={styles.code}>mobile/release-X.Y.Z</code> tag to build prod web plus desktop installers
            </li>
            <li>Mac CI runs on <code style={styles.code}>macos-14</code>; Windows CI runs on <code style={styles.code}>windows-latest</code></li>
            <li>DMG and EXE artifacts are attached to the same GitHub Release as the web deploy</li>
          </ul>
          <h3 style={styles.infoTitle}>Local build</h3>
          <pre style={styles.pre}>
{`# Dev build
./scripts/electron-build.sh dev

# Prod build
./scripts/electron-build.sh prod

# Output on current OS: release/*.dmg or release/*.exe`}
          </pre>
        </div>

        {error && <p style={styles.errorText}>{error}</p>}

        <p style={styles.footer}>
          <a href={GITHUB_RELEASES_URL} style={styles.link} target="_blank" rel="noreferrer">
            All Releases on GitHub
          </a>
        </p>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  page: {
    minHeight: "100vh",
    background: "#fafafa",
    display: "flex",
    justifyContent: "center",
    padding: "48px 16px",
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  },
  container: {
    maxWidth: 600,
    width: "100%",
  },
  heading: {
    fontSize: 28,
    fontWeight: 700,
    color: "#111",
    margin: 0,
  },
  subtitle: {
    fontSize: 14,
    color: "#888",
    marginTop: 4,
    marginBottom: 32,
  },
  loadingText: {
    color: "#888",
    fontSize: 14,
  },
  card: {
    background: "#fff",
    borderRadius: 12,
    padding: 24,
    marginBottom: 16,
    border: "1px solid #eee",
  },
  cardHeader: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    marginBottom: 8,
  },
  cardTitle: {
    fontSize: 18,
    fontWeight: 600,
    color: "#111",
    margin: 0,
  },
  cardDesc: {
    fontSize: 14,
    color: "#666",
    marginBottom: 16,
    lineHeight: 1.5,
  },
  badge: {
    fontSize: 11,
    fontWeight: 700,
    padding: "2px 8px",
    borderRadius: 4,
    letterSpacing: 0.5,
  },
  badgeDev: {
    background: "#fff3cd",
    color: "#856404",
  },
  badgeProd: {
    background: "#d4edda",
    color: "#155724",
  },
  releaseInfo: {},
  releaseTag: {
    fontSize: 13,
    color: "#888",
    marginBottom: 12,
  },
  buttonRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    flexWrap: "wrap" as const,
  },
  button: {
    display: "inline-block",
    padding: "8px 20px",
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 600,
    textDecoration: "none",
    cursor: "pointer",
    transition: "opacity 0.15s",
  },
  buttonPrimary: {
    background: "#111",
    color: "#fff",
  },
  buttonSecondary: {
    background: "#f0f0f0",
    color: "#333",
  },
  link: {
    fontSize: 13,
    color: "#4ea4f5",
    textDecoration: "none",
  },
  infoSection: {
    marginTop: 24,
    padding: "20px 24px",
    background: "#fff",
    borderRadius: 12,
    border: "1px solid #eee",
  },
  infoTitle: {
    fontSize: 15,
    fontWeight: 600,
    color: "#111",
    margin: "0 0 8px 0",
  },
  infoList: {
    fontSize: 14,
    color: "#444",
    lineHeight: 1.8,
    paddingLeft: 20,
    marginBottom: 20,
  },
  pre: {
    background: "#f5f5f5",
    borderRadius: 8,
    padding: 16,
    fontSize: 13,
    color: "#333",
    overflow: "auto",
    lineHeight: 1.6,
  },
  code: {
    background: "#f0f0f0",
    padding: "1px 5px",
    borderRadius: 4,
    fontSize: 13,
  },
  errorText: {
    fontSize: 13,
    color: "#cc6600",
    marginTop: 12,
  },
  footer: {
    marginTop: 24,
    textAlign: "center" as const,
  },
};

export default DownloadDesktop;
